import { encryptText, decryptText } from "commonmethods/Encryption.js";

// Database configuration
const DB_NAME = "BrainsieComGuestCart";
const DB_VERSION = 1;
const STORE_NAME = "guestCart";

/**
 * Initialize IndexedDB for guest cart
 * Creates the database and object store if they don't exist
 */
export const initGuestCartDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("Failed to open IndexedDB:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create guest cart object store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: false,
        });

        // Create indexes for efficient querying
        store.createIndex("Product_Id", "Product_Id", { unique: false });
        store.createIndex("Combination_Id", "Combination_Id", {
          unique: false,
        });
        store.createIndex("Created_On", "Created_On", { unique: false });
        store.createIndex("choice_options_hash", "choice_options_hash", {
          unique: false,
        });
      }
    };
  });
};

/**
 * Generate unique ID for cart items
 */
const generateCartItemId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Generate hash for choice options to enable duplicate detection
 * @param {Object} choiceOptions - Choice options object
 * @returns {string} MD5-like hash of choice options
 */
const generateChoiceOptionsHash = (choiceOptions) => {
  if (!choiceOptions || Object.keys(choiceOptions).length === 0) {
    return "";
  }

  // Sort keys to ensure consistent hashing
  const sortedKeys = Object.keys(choiceOptions).sort();
  const hashString = sortedKeys
    .map((key) => {
      const value = choiceOptions[key];
      if (Array.isArray(value)) {
        return `${key}:${value.sort().join(",")}`;
      }
      return `${key}:${value}`;
    })
    .join("|");

  // Simple hash function (not cryptographic, just for duplicate detection)
  let hash = 0;
  for (let i = 0; i < hashString.length; i++) {
    const char = hashString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
};

/**
 * Generate display text for choice options
 * @param {Object} choiceOptions - Choice options object
 * @returns {string} Human-readable choice options text
 */
const generateChoiceDisplayText = (choiceOptions) => {
  if (!choiceOptions || Object.keys(choiceOptions).length === 0) {
    return "";
  }

  return Object.entries(choiceOptions)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}: ${value.join(", ")}`;
      }
      return `${key}: ${value}`;
    })
    .join(", ");
};

/**
 * Create a standardized guest cart item object
 * @param {Object} params - Cart item parameters
 * @returns {Object} Standardized cart item - matches authenticated cart structure
 */
const createGuestCartItem = ({
  productId,
  combinationId,
  units,
  pricePerUnit,
  choiceOptions = {},
  selectedAddons = [],
  isSaveForLater = "0",
  productDetails = {},
}) => {
  const id = generateCartItemId();
  const timestamp = new Date().toISOString();
  const choiceOptionsHash = generateChoiceOptionsHash(choiceOptions);
  const displayChoiceText = generateChoiceDisplayText(choiceOptions);

  // Get first image from product details and format it properly
  const imageArray =
    productDetails.Product_Images || productDetails.Default_Media || [];
  const firstImage =
    Array.isArray(imageArray) && imageArray.length > 0 ? imageArray[0] : null;

  // Create proper image object structure
  const imageObj = firstImage
    ? {
        Media_File_Type: "image",
        Media_File_Path:
          typeof firstImage === "string"
            ? firstImage
            : firstImage.Media_File_Path || firstImage.url || firstImage,
        Is_Deleted: "0",
      }
    : null;

  // Convert Product_Images or Default_Media to Media array format (matching authenticated cart)
  const sourceImages =
    productDetails.Product_Images || productDetails.Default_Media || [];
  const mediaArray = Array.isArray(sourceImages)
    ? sourceImages.map((img, index) => ({
        Media_File_Type: "image",
        Media_File_Path:
          typeof img === "string" ? img : img.Media_File_Path || img.url || img,
        Is_Deleted: "0",
        Media_File_Name: `image_${index}`,
      }))
    : [];

  return {
    // Core cart identifiers (match authenticated cart)
    Cart_Id: id, // Use our generated ID as Cart_Id for consistency
    id, // Keep id for internal guest cart operations
    User_Id: "guest", // Mark as guest user
    Product_Id: productId,
    Combination_Id: combinationId,
    Units: units,
    Is_Savefor_Later: isSaveForLater,
    Added_On: timestamp,

    // Product display data (match authenticated cart structure)
    Product_Full_Name: productDetails.Product_Full_Name || "",
    Short_Description: productDetails.Short_Description || "",
    Combination_Text: productDetails.Combination_Text || "",
    Price_Per_Unit: pricePerUnit,
    price: pricePerUnit, // Both fields for compatibility
    Total_Price: units * pricePerUnit,
    image: imageObj ? imageObj.Media_File_Path : null,
    selected: true, // Default to selected
    Choice_Options: choiceOptions || {},

    // Product status flags (match authenticated cart)
    isProductDeleted: productDetails.Is_Deleted === "1",
    isCombinationDeleted: productDetails.Combination?.Is_Deleted === "1",
    isCombinationSoldOut:
      productDetails.Is_Soldout === "1" ||
      productDetails.Combination?.Is_Soldout === "1",
    isInventoryDeleted: productDetails.Inventory?.Is_Deleted === "1",
    isOutOfStock:
      !productDetails.Inventory ||
      productDetails.Current_Stock <= 0 ||
      productDetails.Inventory?.Is_Deleted === "1",
    isCurrentlyUnavailable: false, // Default to false for guest cart

    // Store full objects for compatibility with authenticated cart
    Media: mediaArray,
    Default_Media:
      productDetails.Default_Media ||
      mediaArray.map((img) => img.Media_File_Path), // Array of image paths for ProductCard compatibility
    Product_Images:
      productDetails.Product_Images || productDetails.Default_Media || [], // Keep original Product_Images
    Product_Image_1:
      productDetails.Product_Image_1 ||
      (imageObj ? imageObj.Media_File_Path : null), // Add Product_Image_1 for GuestOrderSummary compatibility
    Inventory: productDetails.Inventory ||
      productDetails.Default_Inventory || {
        Total_Selling_Price: pricePerUnit,
        Total_MRP_Price: productDetails.Total_MRP_Price || pricePerUnit,
        Current_Stock: productDetails.Current_Stock || 999,
        Total_Taxable_Percentage: productDetails.Total_Taxable_Percentage || 0,
        Is_Deleted: "0",
      },
    Product: productDetails.Product || {
      Product_Full_Name: productDetails.Product_Full_Name,
      Short_Description: productDetails.Short_Description,
      Is_Deleted: productDetails.Is_Deleted || "0",
    },
    Combination: productDetails.Combination ||
      productDetails.Default_Combination || {
        Combination_Id: combinationId,
        Combination_Text: productDetails.Combination_Text || "Default",
        Is_Deleted: "0",
        Is_Soldout: productDetails.Is_Soldout || "0",
      },
    Currently_Unavailable: false,

    // Internal guest cart fields
    Selected_Addons: selectedAddons,
    Created_On: timestamp,
    Modified_On: timestamp,
    choice_options_hash: choiceOptionsHash,
    display_choice_text: displayChoiceText,
    productDetails, // Store original productDetails for backward compatibility
  };
};

/**
 * Get database connection
 */
const getDB = async () => {
  return await initGuestCartDB();
};

/**
 * Get all guest cart items
 * @returns {Promise<Array>} Array of cart items
 */
export const getAllGuestCartItems = async () => {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const items = (request.result || []).map((item) => ({
          ...item,
          // Ensure Choice_Options is always an object, not array or null
          Choice_Options:
            item.Choice_Options &&
            typeof item.Choice_Options === "object" &&
            !Array.isArray(item.Choice_Options)
              ? item.Choice_Options
              : {},
        }));
        resolve(items);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error getting guest cart items:", error);
    return [];
  }
};

/**
 * Add item to guest cart
 * @param {Object} cartItem - Cart item object
 * @returns {Promise<string>} Item ID
 */
export const addGuestCartItem = async (cartItem) => {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    const item = createGuestCartItem(cartItem);
    const request = store.add(item);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(item.id);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error adding guest cart item:", error);
    throw error;
  }
};

/**
 * Update guest cart item
 * @param {string} itemId - Item ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<boolean>} Success status
 */
export const updateGuestCartItem = async (itemId, updates) => {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    // Get existing item
    const getRequest = store.get(itemId);

    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const existingItem = getRequest.result;
        if (!existingItem) {
          reject(new Error("Item not found"));
          return;
        }

        // Update item with new values
        const updatedItem = {
          ...existingItem,
          ...updates,
          Modified_On: new Date().toISOString(),
        };

        if (
          updates.Units !== undefined ||
          updates.quantity !== undefined
        ) {
          const newUnits = updates.Units !== undefined ? updates.Units : updates.quantity;
          
          // Ensure we update units
          updatedItem.Units = newUnits;
          
          // CRITICAL FIX: Ensure Price_Per_Unit is actually the Unit Price, not a Total Price
          // If updates include a new Price_Per_Unit, use it.
          // Otherwise, try to find a trustworthy price from the embedded Inventory/ProductDetails master data
          // Finally, fallback to existing Price_Per_Unit
          let safeUnitPrice = undefined;
          
          if (updates.Price_Per_Unit !== undefined) {
             safeUnitPrice = parseFloat(updates.Price_Per_Unit);
          } else {
             // Try to find safe price in nested objects to recover from potential data corruption
             if (updatedItem.Inventory && updatedItem.Inventory.Total_Selling_Price) {
                 safeUnitPrice = parseFloat(updatedItem.Inventory.Total_Selling_Price);
             } else if (updatedItem.productDetails && updatedItem.productDetails.Inventory && updatedItem.productDetails.Inventory.Total_Selling_Price) {
                 safeUnitPrice = parseFloat(updatedItem.productDetails.Inventory.Total_Selling_Price);
             } else {
                 safeUnitPrice = parseFloat(updatedItem.Price_Per_Unit);
             }
          }
          
          // Self-healing: Update the Price_Per_Unit on the item itself if we found a better source
          // This ensures that if we read a corrupted value but found the master price, we adhere to the master price
          updatedItem.Price_Per_Unit = safeUnitPrice; 
          
          updatedItem.Total_Price = newUnits * safeUnitPrice;
        }

        // Update choice options hash and display text if choice options changed
        if (updates.Choice_Options !== undefined) {
          updatedItem.choice_options_hash = generateChoiceOptionsHash(
            updates.Choice_Options
          );
          updatedItem.display_choice_text = generateChoiceDisplayText(
            updates.Choice_Options
          );
        }

        const putRequest = store.put(updatedItem);
        putRequest.onsuccess = () => {
          resolve(true);
        };
        putRequest.onerror = () => reject(putRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error("Error updating guest cart item:", error);
    throw error;
  }
};

/**
 * Remove item from guest cart
 * @param {string} itemId - Item ID
 * @returns {Promise<boolean>} Success status
 */
export const removeGuestCartItem = async (itemId) => {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(itemId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(true);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error removing guest cart item:", error);
    throw error;
  }
};

/**
 * Find existing cart item with same product and choice options
 * @param {number} productId - Product ID
 * @param {number} combinationId - Combination ID
 * @param {Object} choiceOptions - Choice options
 * @returns {Promise<Object|null>} Existing cart item or null
 */
export const findExistingGuestCartItem = async (
  productId,
  combinationId,
  choiceOptions = {}
) => {
  try {
    const items = await getAllGuestCartItems();
    const targetHash = generateChoiceOptionsHash(choiceOptions);

    return (
      items.find(
        (item) =>
          item.Product_Id === productId &&
          item.Combination_Id === combinationId &&
          item.choice_options_hash === targetHash &&
          item.Is_Savefor_Later === "0"
      ) || null
    );
  } catch (error) {
    console.error("Error finding existing guest cart item:", error);
    return null;
  }
};

/**
 * Clear all guest cart items
 * @returns {Promise<boolean>} Success status
 */
export const clearGuestCart = async () => {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(true);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("Error clearing guest cart:", error);
    throw error;
  }
};

/**
 * Get guest cart count
 * @returns {Promise<number>} Number of items in cart
 */
export const getGuestCartCount = async () => {
  try {
    const items = await getAllGuestCartItems();
    return items.filter((item) => item.Is_Savefor_Later === "0").length;
  } catch (error) {
    console.error("Error getting guest cart count:", error);
    return 0;
  }
};

/**
 * Get guest cart total
 * @returns {Promise<number>} Total cart value
 */
export const getGuestCartTotal = async () => {
  try {
    const items = await getAllGuestCartItems();
    return items
      .filter((item) => item.Is_Savefor_Later === "0")
      .reduce((total, item) => total + item.Total_Price, 0);
  } catch (error) {
    console.error("Error getting guest cart total:", error);
    return 0;
  }
};

// Export utility functions for external use
export {
  generateChoiceOptionsHash,
  generateChoiceDisplayText,
  createGuestCartItem,
};
